/**
 * Monatskommentar portrait sizing for SHV factsheets (PDF + web preview).
 *
 * Longer commentary must leave more horizontal room for text so page 1 does not overflow.
 * Shorter copy can use a larger portrait for visual balance.
 *
 * Tiers are keyed off ~300 / ~400 / ~500 characters (per dominant column / score).
 */

/** Max characters per left/right Monatskommentar column (prevents page-1 overflow). */
export const MONTHLY_COMMENTARY_MAX_CHARS_PER_COLUMN = 600;

/** @deprecated kept for callers; SHV + PVM both use 600. */
export const MONTHLY_COMMENTARY_MAX_CHARS_PER_COLUMN_PVM = 600;

/** PVM: shrink Monatskommentar type when a column exceeds this length. */
export const PVM_MONTHLY_COMMENTARY_FONT_COMPACT_THRESHOLD = 450;

/** PVM Monatskommentar font sizes (PDF pt / web px). */
export const PVM_MONTHLY_COMMENTARY_FONT_SIZE_PDF = 8.0;
export const PVM_MONTHLY_COMMENTARY_FONT_SIZE_PDF_COMPACT = 7.0;
export const PVM_MONTHLY_COMMENTARY_FONT_SIZE_WEB = 12.5;
export const PVM_MONTHLY_COMMENTARY_FONT_SIZE_WEB_COMPACT = 11;

export type MonthlyCommentarySide = 'left' | 'right';

/**
 * Per-template, per-column exceptions to MONTHLY_COMMENTARY_MAX_CHARS_PER_COLUMN.
 * Frankfurter UCITS-ETF – Modern Value (69842241…, only used by the SHV ETF):
 * right column 631 (SHV request 04.10.2026).
 */
const MONTHLY_COMMENTARY_MAX_CHARS_OVERRIDES: Record<
  string,
  Partial<Record<MonthlyCommentarySide, number>>
> = {
  '69842241581723c403c697fc': { right: 631 },
};

export function getMonthlyCommentaryMaxCharsPerColumn(templateId?: string): number {
  void templateId;
  return MONTHLY_COMMENTARY_MAX_CHARS_PER_COLUMN;
}

export function getMonthlyCommentaryMaxChars(
  templateId: string | undefined,
  side: MonthlyCommentarySide,
): number {
  const override = templateId ? MONTHLY_COMMENTARY_MAX_CHARS_OVERRIDES[templateId]?.[side] : undefined;
  return override ?? MONTHLY_COMMENTARY_MAX_CHARS_PER_COLUMN;
}

/**
 * Score used for portrait sizing.
 * Columns sit side-by-side — the longer column drives vertical growth.
 */
export function commentaryLengthScore(left?: string, right?: string): number {
  const leftLen = (left || '').trim().length;
  const rightLen = (right || '').trim().length;
  const dominant = Math.max(leftLen, rightLen);
  const total = leftLen + rightLen;
  return Math.max(dominant, Math.floor(total * 0.45));
}

/** True when PVM Monatskommentar should use the compact (smaller) font. */
export function shouldUsePvmMonthlyCommentaryCompactFont(left?: string, right?: string): boolean {
  return commentaryLengthScore(left, right) > PVM_MONTHLY_COMMENTARY_FONT_COMPACT_THRESHOLD;
}

export function calcPvmMonthlyCommentaryFontSizePdf(left?: string, right?: string): number {
  return shouldUsePvmMonthlyCommentaryCompactFont(left, right)
    ? PVM_MONTHLY_COMMENTARY_FONT_SIZE_PDF_COMPACT
    : PVM_MONTHLY_COMMENTARY_FONT_SIZE_PDF;
}

export function calcPvmMonthlyCommentaryFontSizeWeb(left?: string, right?: string): number {
  return shouldUsePvmMonthlyCommentaryCompactFont(left, right)
    ? PVM_MONTHLY_COMMENTARY_FONT_SIZE_WEB_COMPACT
    : PVM_MONTHLY_COMMENTARY_FONT_SIZE_WEB;
}

type ImageSizeTier = {
  /** PDF width in pt */
  widthPdf: number;
  /** Soft height cap in pt */
  maxHeightPdf: number;
  /** Web preview width in px */
  widthWeb: number;
};

/** ~300 / ~400 / ~500 character bands (plus short + near-max). */
function resolveImageSizeTier(score: number): ImageSizeTier {
  // Short / empty — largest portrait
  if (score <= 300) {
    return { widthPdf: 80, maxHeightPdf: 144, widthWeb: 133 };
  }
  // ~400 chars
  if (score <= 400) {
    return { widthPdf: 76, maxHeightPdf: 137, widthWeb: 127 };
  }
  // ~500 chars
  if (score <= 500) {
    return { widthPdf: 72, maxHeightPdf: 130, widthWeb: 120 };
  }
  // ~500–600 (near limit) — slightly smaller
  return { widthPdf: 68, maxHeightPdf: 123, widthWeb: 113 };
}

/** Default / fallback constants (short-text tier). */
export const MONTHLY_COMMENTARY_IMAGE_WIDTH_PDF = 80;
export const MONTHLY_COMMENTARY_IMAGE_MAX_HEIGHT_PDF = 144;
export const MONTHLY_COMMENTARY_IMAGE_WIDTH_WEB = 133;

export function clampMonthlyCommentaryColumn(
  text: string,
  max = MONTHLY_COMMENTARY_MAX_CHARS_PER_COLUMN,
): string {
  if (!text) return '';
  return text.length > max ? text.slice(0, max) : text;
}

/** PDF width in pt (react-pdf). Longer text → narrower image. */
export function calcMonthlyCommentaryImageWidth(left?: string, right?: string): number {
  return resolveImageSizeTier(commentaryLengthScore(left, right)).widthPdf;
}

/** Soft cap so a tall portrait cannot dominate short commentary. */
export function calcMonthlyCommentaryImageMaxHeight(left?: string, right?: string): number {
  return resolveImageSizeTier(commentaryLengthScore(left, right)).maxHeightPdf;
}

/** Web preview width in px. */
export function calcMonthlyCommentaryImageWidthWeb(left?: string, right?: string): number {
  return resolveImageSizeTier(commentaryLengthScore(left, right)).widthWeb;
}
